import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const API_BASE = `${config.api.baseUrl}/locations`;

// The Supabase Edge Function gateway rejects requests with no auth header
// (401 UNAUTHORIZED_NO_AUTH_HEADER) before they ever reach the function.
// These are public catalog endpoints, so the anon key is sufficient — but
// prefer the signed-in user's token when one exists. Works pre- and
// post-login (e.g. registration and profile setup both use the selector).
function catalogHeaders(): HeadersInit {
  const anon = config.supabase.anonKey;
  const token = authInterceptor.getAccessToken() || anon;
  return { apikey: anon, Authorization: `Bearer ${token}` };
}

export interface CountryOption {
  isoCode: string;
  name: string;
}

export interface StateOption {
  isoCode: string;
  countryCode: string;
  name: string;
}

export interface CityOption {
  id: number;
  countryCode: string;
  stateCode: string | null;
  name: string;
}

function toCountry(row: any): CountryOption {
  return { isoCode: row.iso_code, name: row.name };
}

function toState(row: any): StateOption {
  return { isoCode: row.iso_code, countryCode: row.country_code, name: row.name };
}

function toCity(row: any): CityOption {
  return { id: row.id, countryCode: row.country_code, stateCode: row.state_code, name: row.name };
}

class LocationsService {
  // Countries never change at runtime — fetch once per session and reuse
  // across every mount of the location selector instead of refetching.
  private countriesCache: Promise<CountryOption[]> | null = null;

  async getCountries(): Promise<CountryOption[]> {
    if (!this.countriesCache) {
      this.countriesCache = fetch(`${API_BASE}/countries`, { headers: catalogHeaders() })
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to get countries: ${res.statusText}`);
          return res.json();
        })
        .then(({ data }) => (data ?? []).map(toCountry))
        .catch((err) => {
          this.countriesCache = null; // allow retrying on a later call
          throw err;
        });
    }
    return this.countriesCache;
  }

  async getStates(countryCode: string): Promise<StateOption[]> {
    const response = await fetch(`${API_BASE}/states?country=${encodeURIComponent(countryCode)}`, { headers: catalogHeaders() });
    if (!response.ok) throw new Error(`Failed to get states: ${response.statusText}`);
    const { data } = await response.json();
    return (data ?? []).map(toState);
  }

  async getCities(countryCode: string, stateCode?: string): Promise<CityOption[]> {
    const params = new URLSearchParams({ country: countryCode });
    if (stateCode) params.set('state', stateCode);
    const response = await fetch(`${API_BASE}/cities?${params.toString()}`, { headers: catalogHeaders() });
    if (!response.ok) throw new Error(`Failed to get cities: ${response.statusText}`);
    const { data } = await response.json();
    return (data ?? []).map(toCity);
  }
}

export const locationsService = new LocationsService();
